import { useEffect, useState, useRef } from "react";
import { Bell, BellRing } from "lucide-react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { resolveImageUrl } from "@/lib/api";
import { formatDistanceToNow } from "date-fns";
import { enableWebPush, pushSupported, pushStatus } from "@/lib/push";
import { toast } from "sonner";

function initials(name = "?") { return name.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase(); }
function timeAgo(iso) { try { return formatDistanceToNow(new Date(iso), { addSuffix: true }); } catch { return ""; } }

const PERMISSION_ASKED_KEY = "ayur_push_asked";
const LAST_SEEN_ID_KEY = "ayur_last_seen_notif_id";

async function requestBrowserPushPermission() {
  if (typeof window === "undefined" || !("Notification" in window)) return;
  if (Notification.permission === "granted" || Notification.permission === "denied") return;
  if (sessionStorage.getItem(PERMISSION_ASKED_KEY)) return;
  try {
    const result = await Notification.requestPermission();
    if (result === "granted" || result === "denied") {
      sessionStorage.setItem(PERMISSION_ASKED_KEY, "1");
    }
  } catch {}
}

function fireBrowserNotification(n) {
  if (typeof window === "undefined" || !("Notification" in window)) return;
  if (Notification.permission !== "granted") return;
  try {
    const title = {
      follow: "New follower",
      comment: "New comment",
      rsvp: "New RSVP",
      message: "New message",
      like: "New like",
    }[n.type] || "VaidGram";
    const notif = new Notification(title, {
      body: n.content,
      icon: "/hero.png",
      tag: n.id,
      silent: false,
    });
    notif.onclick = () => { window.focus(); notif.close(); };
  } catch {}
}

export default function Notifications() {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [pushSub, setPushSub] = useState({ supported: false, subscribed: false, permission: "default" });
  const [enabling, setEnabling] = useState(false);
  const timer = useRef(null);
  const seenRef = useRef(new Set());
  const initialisedRef = useRef(false);

  const refreshPushStatus = async () => {
    try { setPushSub(await pushStatus()); } catch {}
  };

  const enablePush = async () => {
    setEnabling(true);
    try {
      const ok = await enableWebPush();
      if (ok) {
        toast.success("Push alerts enabled");
        await refreshPushStatus();
      } else {
        toast.error("Notification permission denied");
      }
    } catch (e) {
      toast.error(e.message || "Could not enable push");
    } finally { setEnabling(false); }
  };

  const load = async () => {
    try {
      const { data } = await api.get("/notifications");
      const notifs = data.notifications || [];
      setItems(notifs);
      setUnread(data.unread || 0);

      // On first load, mark existing ids as seen (no push spam on refresh)
      if (!initialisedRef.current) {
        initialisedRef.current = true;
        notifs.forEach((n) => seenRef.current.add(n.id));
        const lastSeen = localStorage.getItem(LAST_SEEN_ID_KEY);
        if (lastSeen) {
          notifs.forEach((n) => seenRef.current.add(n.id));
        }
        localStorage.setItem(LAST_SEEN_ID_KEY, notifs[0]?.id || "");
        return;
      }

      // Fire browser notification for new unread items we haven't seen before
      for (const n of notifs) {
        if (!n.read && !seenRef.current.has(n.id)) {
          fireBrowserNotification(n);
        }
        seenRef.current.add(n.id);
      }
      if (notifs[0]) localStorage.setItem(LAST_SEEN_ID_KEY, notifs[0].id);
    } catch {}
  };

  useEffect(() => {
    requestBrowserPushPermission();
    load();
    timer.current = setInterval(load, 20000);
    return () => clearInterval(timer.current);
  }, []);

  const markAllRead = async () => {
    if (unread === 0) return;
    try { await api.post("/notifications/read-all"); setUnread(0); } catch {}
  };

  const routeFor = (n) => {
    if (n.type === "message") return `/app/chat/${n.ref_id}`;
    if (n.type === "follow") return `/app/scholars/${n.actor_id}`;
    if (n.type === "rsvp") return `/app/meetups`;
    return `/app`;
  };

  return (
    <Popover open={open} onOpenChange={(v) => { setOpen(v); if (v) markAllRead(); }}>
      <PopoverTrigger asChild>
        <button
          data-testid="notifications-bell"
          className="relative w-9 h-9 rounded-full flex items-center justify-center hover:bg-[color:var(--ayur-cream-2)] transition"
        >
          <Bell className="w-5 h-5 text-[color:var(--ayur-emerald)]" />
          {unread > 0 && (
            <span className="absolute top-1 right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[color:var(--ayur-terracotta)] text-white text-[10px] font-bold flex items-center justify-center">
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0 bg-[color:var(--ayur-cream)] border-[color:var(--ayur-border)]">
        <div className="p-4 border-b border-[color:var(--ayur-border)]">
          <div className="label-eyebrow">Notifications</div>
          <div className="font-serif-display text-lg text-[color:var(--ayur-emerald)]">Recent activity</div>
          {pushSub.supported && !pushSub.subscribed && (
            <button
              onClick={enablePush}
              disabled={enabling}
              data-testid="enable-push-button"
              className="mt-2 inline-flex items-center gap-1 text-xs text-[color:var(--ayur-terracotta)] font-semibold link-underline"
            >
              <BellRing className="w-3.5 h-3.5" />
              {enabling ? "Enabling…" : "Enable offline push alerts"}
            </button>
          )}
          {pushSub.subscribed && (
            <div className="mt-2 inline-flex items-center gap-1 text-xs text-[color:var(--ayur-emerald)]">
              <BellRing className="w-3.5 h-3.5 fill-current" /> Offline push is on
            </div>
          )}
        </div>
        <div className="max-h-96 overflow-y-auto">
          {items.length === 0 && (
            <div className="p-6 text-center text-sm text-[color:var(--ayur-mute)]">All caught up — nothing new.</div>
          )}
          {items.map((n) => (
            <Link
              key={n.id}
              to={routeFor(n)}
              onClick={() => setOpen(false)}
              data-testid="notification-item"
              className={`flex items-start gap-3 px-4 py-3 hover:bg-white transition ${!n.read ? "bg-[color:var(--ayur-cream-2)]" : ""}`}
            >
              <Avatar className="w-8 h-8">
                <AvatarImage src={resolveImageUrl(n.actor?.avatar_url)} />
                <AvatarFallback className="bg-[color:var(--ayur-badge)] text-[color:var(--ayur-emerald)] text-xs">
                  {initials(n.actor?.full_name)}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <div className="text-sm text-[color:var(--ayur-ink)] truncate">{n.content}</div>
                <div className="text-xs text-[color:var(--ayur-mute)]">{timeAgo(n.created_at)}</div>
              </div>
            </Link>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
